import React, { useMemo, useState } from 'react'

import AnalyticsChart from 'components/AnalyticsChart'
import AnalyticsKpis from 'components/AnalyticsKpis'
import BasicTable from 'components/Form/Table'
import {
  ChartPanel,
  Grid,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Ranking,
  RankingItem,
  RankingMeta,
  RankingTitle,
  SectionRoot,
  TablePanel,
  TwoColumns,
} from './styled'

const KPI_CONFIG = {
  overview: [
    ['activeUsers', 'number', 'analytics_metric_active_user_help'],
    ['sessions', 'number', 'analytics_metric_session_help'],
    ['returningUsers', 'number'],
    ['contentViews', 'number', 'analytics_metric_views_help'],
    ['coursesCompleted', 'number', 'analytics_metric_course_completed_help'],
    ['videoViews', 'number'],
    ['downloads', 'number'],
    ['certificatesIssued', 'number', 'analytics_metric_certificate_issued_help'],
    ['engagementRate', 'percent'],
    ['aiInteractions', 'number'],
  ],
  activity: [
    ['activeUsers', 'number', 'analytics_metric_active_user_help'],
    ['sessions', 'number', 'analytics_metric_session_help'],
    ['returningUsers', 'number'],
    ['contentViews', 'number', 'analytics_metric_views_help'],
  ],
  learning: [
    ['views', 'number'], ['uniqueViewers', 'number'], ['usersStarted', 'number'], ['usersCompleted', 'number'],
    ['completionRate', 'percent', 'analytics_metric_completion_rate_help'], ['averageProgress', 'percent'],
    ['certificatesIssued', 'number'], ['certificationRate', 'percent', 'analytics_metric_certification_rate_help'],
  ],
  videos: [
    ['plays', 'number'], ['uniqueViewers', 'number'], ['averageWatchPercentage', 'percent'],
    ['completedViews', 'number'], ['completionRate', 'percent'],
  ],
  files: [['totalDownloads', 'number'], ['uniqueDownloaders', 'number'], ['filesWithoutDownloads', 'number']],
  onboarding: [
    ['eligible', 'number'], ['loggedIn', 'number'], ['opened', 'number'], ['watched', 'number'],
    ['completed', 'number'], ['loggedInWithoutWatch', 'number'],
  ],
  certificates: [
    ['certificatesIssued', 'number'], ['eligibleCourseCompletions', 'number'], ['certificationRate', 'percent'],
    ['automaticCertificates', 'number'], ['trainerApprovalCertificates', 'number'], ['pendingApprovals', 'number'], ['approved', 'number'], ['rejected', 'number'],
  ],
  users: [
    ['dau', 'number'], ['wau', 'number'], ['mau', 'number'], ['activeUsers', 'number'], ['newUsers', 'number'],
    ['returningUsers', 'number'], ['sessionsPerUser', 'number'], ['averageSessionDuration', 'duration'], ['inactiveUsers', 'number'],
  ],
  competitive: [
    ['activeUsers', 'number'], ['sessions', 'number'], ['totalViews', 'number'], ['uniqueViews', 'number'],
    ['searches', 'number'], ['aiConversations', 'number'], ['aiPrompts', 'number'], ['sourceOpens', 'number'],
    ['averageSessionDuration', 'duration'],
  ],
  invitations: [
    ['invitationsCreated', 'number'], ['invitationsUsed', 'number'], ['registrations', 'number'],
    ['firstAccesses', 'number'], ['activatedUsers', 'number'], ['engagedUsers', 'number'],
    ['activationRate', 'percent'], ['engagementRate', 'percent'],
  ],
  surveys: [
    ['totalSurveys', 'number'], ['surveyViews', 'number'], ['surveyStarts', 'number'],
    ['surveyCompletions', 'number'], ['responses', 'number'], ['uniqueParticipants', 'number'],
    ['completionRate', 'percent'], ['abandonmentRate', 'percent'], ['averageCompletionTime', 'duration'],
  ],
}

const KPI_LABELS = {
  activeUsers: 'analytics_kpi_active_users',
  sessions: 'analytics_kpi_sessions',
  newUsers: 'analytics_kpi_new_users',
  returningUsers: 'analytics_kpi_returning_users',
  contentViews: 'analytics_kpi_content_views',
  coursesStarted: 'analytics_kpi_courses_started',
  coursesCompleted: 'analytics_kpi_courses_completed',
  videoViews: 'analytics_kpi_video_views',
  downloads: 'analytics_kpi_downloads',
  certificatesIssued: 'analytics_kpi_certificates_issued',
  issued: 'analytics_kpi_certificates_issued',
  averageSessionDuration: 'analytics_kpi_average_session_duration',
  averageSessionDurationSeconds: 'analytics_kpi_average_session_duration',
  completionRate: 'analytics_kpi_completion_rate',
  views: 'analytics_kpi_views',
  uniqueViewers: 'analytics_kpi_unique_viewers',
  usersStarted: 'analytics_kpi_courses_started',
  usersCompleted: 'analytics_kpi_courses_completed',
  averageProgress: 'analytics_kpi_average_progress',
  certificationRate: 'analytics_kpi_certification_rate',
  plays: 'analytics_kpi_video_plays',
  averageWatchPercentage: 'analytics_kpi_average_watch_percentage',
  completedViews: 'analytics_kpi_completed_views',
  totalDownloads: 'analytics_kpi_downloads',
  uniqueDownloaders: 'analytics_kpi_unique_downloaders',
  filesWithoutDownloads: 'analytics_kpi_files_without_downloads',
  eligible: 'analytics_kpi_eligible_users',
  loggedIn: 'analytics_kpi_logged_in',
  opened: 'analytics_kpi_onboarding_opened',
  watched: 'analytics_kpi_onboarding_watched',
  completed: 'analytics_kpi_onboarding_completed',
  loggedInNotWatched: 'analytics_kpi_logged_in_not_watched',
  loggedInWithoutWatch: 'analytics_kpi_logged_in_not_watched',
  eligibleCompletions: 'analytics_kpi_eligible_completions',
  eligibleCourseCompletions: 'analytics_kpi_eligible_completions',
  automatic: 'analytics_kpi_automatic_certificates',
  automaticCertificates: 'analytics_kpi_automatic_certificates',
  trainerApprovalCertificates: 'analytics_kpi_trainer_approval_certificates',
  pending: 'analytics_kpi_pending_approvals',
  pendingApprovals: 'analytics_kpi_pending_approvals',
  approved: 'analytics_kpi_approved',
  rejected: 'analytics_kpi_rejected',
  dau: 'analytics_kpi_dau',
  wau: 'analytics_kpi_wau',
  mau: 'analytics_kpi_mau',
  sessionsPerUser: 'analytics_kpi_sessions_per_user',
  inactiveUsers: 'analytics_kpi_inactive_users',
  totalViews: 'analytics_kpi_total_views',
  uniqueViews: 'analytics_kpi_unique_views',
  searches: 'analytics_kpi_searches',
  aiConversations: 'analytics_kpi_ai_conversations',
  aiPrompts: 'analytics_kpi_ai_prompts',
  sourceOpens: 'analytics_kpi_source_opens',
  engagementRate: 'analytics_kpi_engagement_rate',
  aiInteractions: 'analytics_column_ai_interactions',
  invitationsCreated: 'analytics_kpi_invitations_created',
  invitationsUsed: 'analytics_kpi_invitations_used',
  registrations: 'analytics_kpi_registrations',
  firstAccesses: 'analytics_kpi_first_accesses',
  activatedUsers: 'analytics_kpi_activated_users',
  engagedUsers: 'analytics_kpi_engaged_users',
  activationRate: 'analytics_kpi_activation_rate',
  totalSurveys: 'analytics_kpi_total_surveys',
  surveyViews: 'analytics_kpi_survey_views',
  surveyStarts: 'analytics_kpi_survey_starts',
  surveyCompletions: 'analytics_kpi_survey_completions',
  responses: 'analytics_kpi_survey_responses',
  uniqueParticipants: 'analytics_kpi_unique_participants',
  abandonmentRate: 'analytics_kpi_abandonment_rate',
  averageCompletionTime: 'analytics_kpi_average_completion_time',
}

const FUNNEL_LABELS = {
  eligible: 'analytics_funnel_eligible',
  loggedIn: 'analytics_funnel_logged_in',
  opened: 'analytics_funnel_opened',
  watched: 'analytics_funnel_watched',
  viewed: 'analytics_funnel_viewed',
  started: 'analytics_funnel_started',
  25: 'analytics_funnel_reached_25',
  50: 'analytics_funnel_reached_50',
  75: 'analytics_funnel_reached_75',
  100: 'analytics_funnel_reached_100',
  completed: 'analytics_funnel_completed',
  certified: 'analytics_funnel_certified',
  created: 'analytics_funnel_invitation_created',
  used: 'analytics_funnel_invitation_used',
  registered: 'analytics_funnel_registered',
  firstAccess: 'analytics_funnel_first_access',
  activated: 'analytics_funnel_activated',
  engaged: 'analytics_funnel_engaged',
  responded: 'analytics_kpi_survey_responses',
}

const ACTIVITY_ACTION_KEYS = {
  viewed: 'analytics_activity_viewed',
  started: 'analytics_activity_started',
  completed: 'analytics_activity_completed',
  downloaded: 'analytics_activity_downloaded',
  certificate_issued: 'analytics_activity_certificate_issued',
  logged_in: 'analytics_activity_logged_in',
  session_started: 'analytics_activity_session_started',
}

const ACTIVITY_BREAKDOWN_LABELS = {
  viewed: 'analytics_funnel_viewed',
  started: 'analytics_funnel_started',
  completed: 'analytics_funnel_completed',
  downloaded: 'analytics_kpi_downloads',
  certificate_issued: 'analytics_kpi_certificates_issued',
  logged_in: 'analytics_funnel_logged_in',
  session_started: 'analytics_kpi_sessions',
  activity: 'analytics_column_activities',
}

const PORTAL_LABELS = {
  universe: 'analytics_portal_universe',
  lite: 'analytics_portal_lite',
  accelerator: 'analytics_portal_accelerator',
}

export const activityAction = item => {
  const action = String(item.action || '').toLowerCase().replace(/-/g, '_')
  if (ACTIVITY_ACTION_KEYS[action]) return action

  const eventType = String(item.eventType || item.technicalName || '').toUpperCase()
  if (eventType.includes('CERTIFICATE') && eventType.includes('ISSUED')) return 'certificate_issued'
  if (eventType === 'LOGIN') return 'logged_in'
  if (eventType === 'SESSION_START') return 'session_started'
  if (eventType.includes('DOWNLOAD')) return 'downloaded'
  if (eventType.includes('COMPLETE')) return 'completed'
  if (eventType.includes('START') || eventType.includes('PLAY')) return 'started'
  return 'viewed'
}

export const localizeActivityLabel = (item, t) => {
  const action = activityAction(item)
  const template = t(ACTIVITY_ACTION_KEYS[action])
  if (action === 'certificate_issued') return template

  const content = item.contentLabel || item.content?.title || item.pageTitle || t('analytics_column_content')
  return template.replace('{content}', content)
}

const first = (object, keys, fallback = 0) => {
  const key = keys.find(item => object?.[item] !== undefined && object?.[item] !== null)
  return key ? object[key] : fallback
}

const title = row => first(row, ['title', 'name', 'course', 'video', 'file', 'label'], '—')

const normalizeFunnel = funnel => (
  Array.isArray(funnel)
    ? funnel.map(item => ({ key: item.key || item.label || item.name, value: Number(item.value ?? item.count ?? 0) }))
    : Object.entries(funnel || {}).map(([key, value]) => ({ key, value: Number(value || 0) }))
)

const buildSeries = (rows, definitions) => definitions.map(([key, name]) => ({
  name,
  data: (rows || []).map(row => Number(first(row, [key], 0))),
}))

const personaActivitySeries = (rows, t) => [
  ['viewed', t('analytics_funnel_viewed')],
  ['started', t('analytics_funnel_started')],
  ['completed', t('analytics_funnel_completed')],
  ['downloaded', t('analytics_kpi_downloads')],
  ['other', t('analytics_activity_other')],
].map(([key, name]) => ({
  name,
  data: (rows || []).map(row => Number(row.activityTypes?.[key] || 0)),
}))

export default function AnalyticsSectionContent({
  data = {},
  filters = {},
  formatDate,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
  section,
  setSort,
  t,
}) {
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const kpiSource = section === 'activity' ? data.activityOverview?.kpis : data.kpis
  const kpis = useMemo(() => (KPI_CONFIG[section] || [])
    .filter(([key]) => Object.prototype.hasOwnProperty.call(kpiSource || {}, key))
    .map(([key, type, helpKey]) => ({
      key,
      labelKey: KPI_LABELS[key],
      helpKey,
      formattedValue: kpiSource[key] === null || kpiSource[key] === undefined
        ? '—'
        : type === 'percent'
        ? formatPercent(kpiSource[key])
        : type === 'duration'
          ? formatDuration(kpiSource[key])
          : formatNumber(kpiSource[key]),
    })), [formatDuration, formatNumber, formatPercent, kpiSource, section])

  const certificateJourney = useMemo(() => [
    { key: 'completed', value: Number(data.certificates?.kpis?.eligibleCourseCompletions ?? data.kpis?.coursesCompleted ?? 0) },
    { key: 'certified', value: Number(data.certificates?.kpis?.certificatesIssued ?? data.kpis?.certificatesIssued ?? 0) },
  ], [data.certificates, data.kpis])

  const learningColumns = useMemo(() => [
    { title: t('analytics_column_course'), ref: 'courseTitle', sortable: true },
    { title: t('analytics_column_area'), ref: 'area', sortable: true },
    { title: t('analytics_column_views'), ref: 'views', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['views'])) },
    { title: t('analytics_column_unique_users'), ref: 'uniqueViewers', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['uniqueViewers', 'uniqueUsers'])) },
    { title: t('analytics_column_started'), ref: 'usersStarted', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['usersStarted', 'started'])) },
    { title: t('analytics_column_completed'), ref: 'usersCompleted', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['usersCompleted', 'completed'])) },
    { title: t('analytics_column_completion'), ref: 'completionRate', sortable: true, renderCell: ({ row }) => formatPercent(first(row, ['completionRate'])) },
    { title: t('analytics_column_certificates'), ref: 'certificatesIssued', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['certificatesIssued', 'certificates'])) },
  ], [formatNumber, formatPercent, t])

  const videoColumns = useMemo(() => [
    { title: t('analytics_column_video'), ref: 'title', sortable: true },
    { title: t('analytics_column_area'), ref: 'area', sortable: true },
    { title: t('analytics_column_course'), ref: 'course' },
    { title: t('analytics_column_lesson'), ref: 'lesson' },
    { title: t('analytics_column_plays'), ref: 'plays', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['plays'])) },
    { title: t('analytics_column_unique_users'), ref: 'uniqueViewers', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['uniqueViewers'])) },
    { title: t('analytics_column_average_watch'), ref: 'averageWatchPercentage', sortable: true, renderCell: ({ row }) => formatPercent(first(row, ['averageWatchPercentage', 'averageWatchPercent'])) },
    { title: t('analytics_column_completion'), ref: 'completionRate', sortable: true, renderCell: ({ row }) => formatPercent(first(row, ['completionRate'])) },
  ], [formatNumber, formatPercent, t])

  const fileColumns = useMemo(() => [
    { title: t('analytics_column_file'), ref: 'title', sortable: true },
    { title: t('analytics_column_area'), ref: 'area', sortable: true },
    { title: t('analytics_column_course'), ref: 'course' },
    { title: t('analytics_column_downloads'), ref: 'downloads', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['downloads', 'totalDownloads'])) },
    { title: t('analytics_column_unique_users'), ref: 'uniqueDownloaders', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['uniqueDownloaders', 'uniqueUsers'])) },
    { title: t('analytics_column_last_download'), ref: 'lastDownload', sortable: true, renderCell: ({ row }) => formatDateTime(first(row, ['lastDownload', 'lastDownloadedAt'], null)) },
  ], [formatDateTime, formatNumber, t])

  const certificateColumns = useMemo(() => [
    { title: t('analytics_column_user'), ref: 'userName', sortable: true },
    { title: t('analytics_column_email'), ref: 'userEmail' },
    { title: t('analytics_column_course'), ref: 'courseTitle', sortable: true },
    { title: t('analytics_column_program'), ref: 'program' },
    { title: t('analytics_column_trainer'), ref: 'trainer' },
    { title: t('analytics_column_area'), ref: 'area' },
    { title: t('analytics_column_certificate_type'), ref: 'type' },
    { title: t('analytics_column_status'), ref: 'status' },
    { title: t('analytics_column_issued_date'), ref: 'issuedAt', renderCell: ({ row }) => formatDateTime(row.issuedAt) },
    { title: t('analytics_column_id'), ref: 'certificateId' },
  ], [formatDateTime, t])

  const onboardingColumns = useMemo(() => [
    { title: t('analytics_column_user'), ref: 'displayName' },
    { title: t('analytics_column_persona'), ref: 'persona' },
    { title: t('analytics_column_locale'), ref: 'locale' },
    { title: t('analytics_column_last_login'), ref: 'lastLogin', renderCell: ({ row }) => formatDateTime(first(row, ['lastLogin', 'lastLoginAt'], null)) },
  ], [formatDateTime, t])

  const userColumns = useMemo(() => [
    { title: t('analytics_column_user'), ref: 'displayName', sortable: true },
    { title: t('analytics_column_persona'), ref: 'persona' },
    { title: t('analytics_column_sessions'), ref: 'sessions', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['sessions'])) },
    { title: t('analytics_column_activities'), ref: 'activities', sortable: true, renderCell: ({ row }) => formatNumber(first(row, ['activities', 'events'])) },
    { title: t('analytics_column_last_active'), ref: 'lastActive', sortable: true, renderCell: ({ row }) => formatDateTime(first(row, ['lastActive', 'lastAccess'], null)) },
  ], [formatDateTime, formatNumber, t])

  const competitiveContentColumns = useMemo(() => [
    { title: t('analytics_column_content'), ref: 'title', sortable: true },
    { title: t('analytics_column_content_type'), ref: 'contentType', sortable: true },
    { title: t('analytics_column_competitor'), ref: 'competitor', sortable: true },
    { title: t('analytics_column_source'), ref: 'source', sortable: true },
    { title: t('analytics_column_views'), ref: 'views', sortable: true, renderCell: ({ row }) => formatNumber(row.views) },
    { title: t('analytics_kpi_unique_views'), ref: 'uniqueViews', sortable: true, renderCell: ({ row }) => formatNumber(row.uniqueViews) },
    { title: t('analytics_kpi_source_opens'), ref: 'sourceOpens', sortable: true, renderCell: ({ row }) => formatNumber(row.sourceOpens) },
    { title: t('analytics_column_ai_interactions'), ref: 'aiInteractions', sortable: true, renderCell: ({ row }) => formatNumber(row.aiInteractions) },
  ], [formatNumber, t])

  const assistantColumns = useMemo(() => [
    { title: t('analytics_filter_assistant'), ref: 'assistantId' },
    { title: t('analytics_column_ai_models'), ref: 'models', renderCell: ({ row }) => (row.models || []).join(', ') || '—' },
    { title: t('analytics_column_sessions'), ref: 'sessions', renderCell: ({ row }) => formatNumber(row.sessions) },
    { title: t('analytics_kpi_ai_prompts'), ref: 'prompts', renderCell: ({ row }) => formatNumber(row.prompts) },
    { title: t('analytics_column_responses'), ref: 'responses', renderCell: ({ row }) => formatNumber(row.responses) },
    { title: t('analytics_column_errors'), ref: 'errors', renderCell: ({ row }) => formatNumber(row.errors) },
    { title: t('analytics_column_citation_opens'), ref: 'citationOpens', renderCell: ({ row }) => formatNumber(row.citationOpens) },
    { title: t('analytics_column_ai_units'), ref: 'totalUnits', renderCell: ({ row }) => formatNumber(row.totalUnits) },
    { title: t('analytics_column_average_latency'), ref: 'averageLatencySeconds', renderCell: ({ row }) => formatDuration(row.averageLatencySeconds) },
  ], [formatDuration, formatNumber, t])

  const competitiveFilterColumns = useMemo(() => [
    { title: t('analytics_column_section'), ref: 'section' },
    { title: t('analytics_column_filter_type'), ref: 'filterType' },
    { title: t('analytics_column_filter_value'), ref: 'filterValue' },
    { title: t('analytics_column_filter_applied'), ref: 'applied', renderCell: ({ row }) => formatNumber(row.applied) },
    { title: t('analytics_column_filter_removed'), ref: 'removed', renderCell: ({ row }) => formatNumber(row.removed) },
  ], [formatNumber, t])

  const invitationColumns = useMemo(() => [
    { title: t('analytics_column_invitation'), ref: 'invitation', sortable: true },
    { title: t('analytics_column_area'), ref: 'area', sortable: true },
    { title: t('analytics_column_creator'), ref: 'creator', sortable: true },
    { title: t('analytics_column_status'), ref: 'status' },
    { title: t('analytics_kpi_registrations'), ref: 'registrations', sortable: true, renderCell: ({ row }) => formatNumber(row.registrations) },
    { title: t('analytics_kpi_activated_users'), ref: 'activated', sortable: true, renderCell: ({ row }) => formatNumber(row.activated) },
    { title: t('analytics_kpi_engaged_users'), ref: 'engaged', sortable: true, renderCell: ({ row }) => formatNumber(row.engaged) },
    { title: t('analytics_kpi_activation_rate'), ref: 'activationRate', sortable: true, renderCell: ({ row }) => formatPercent(row.activationRate) },
    { title: t('analytics_kpi_engagement_rate'), ref: 'engagementRate', sortable: true, renderCell: ({ row }) => formatPercent(row.engagementRate) },
  ], [formatNumber, formatPercent, t])

  const creatorColumns = useMemo(() => [
    { title: t('analytics_column_creator'), ref: 'creator' },
    { title: t('analytics_column_area'), ref: 'area' },
    { title: t('analytics_kpi_invitations_created'), ref: 'invitationsCreated', renderCell: ({ row }) => formatNumber(row.invitationsCreated) },
    { title: t('analytics_kpi_registrations'), ref: 'registrations', renderCell: ({ row }) => formatNumber(row.registrations) },
    { title: t('analytics_kpi_activated_users'), ref: 'activatedUsers', renderCell: ({ row }) => formatNumber(row.activatedUsers) },
    { title: t('analytics_kpi_engaged_users'), ref: 'engagedUsers', renderCell: ({ row }) => formatNumber(row.engagedUsers) },
  ], [formatNumber, t])

  const surveyColumns = useMemo(() => [
    { title: t('analytics_filter_survey'), ref: 'survey', sortable: true },
    { title: t('analytics_column_area'), ref: 'area', sortable: true },
    { title: t('analytics_column_course'), ref: 'course', sortable: true },
    { title: t('analytics_kpi_survey_views'), ref: 'views', sortable: true, renderCell: ({ row }) => formatNumber(row.views) },
    { title: t('analytics_kpi_survey_starts'), ref: 'starts', sortable: true, renderCell: ({ row }) => formatNumber(row.starts) },
    { title: t('analytics_kpi_survey_completions'), ref: 'completions', sortable: true, renderCell: ({ row }) => formatNumber(row.completions) },
    { title: t('analytics_kpi_survey_responses'), ref: 'responses', sortable: true, renderCell: ({ row }) => formatNumber(row.responses) },
    { title: t('analytics_kpi_unique_participants'), ref: 'uniqueParticipants', sortable: true, renderCell: ({ row }) => formatNumber(row.uniqueParticipants) },
    { title: t('analytics_kpi_completion_rate'), ref: 'completionRate', sortable: true, renderCell: ({ row }) => formatPercent(row.completionRate) },
    { title: t('analytics_kpi_abandonment_rate'), ref: 'abandonmentRate', sortable: true, renderCell: ({ row }) => formatPercent(row.abandonmentRate) },
  ], [formatNumber, formatPercent, t])

  const surveyQuestionColumns = useMemo(() => [
    { title: t('analytics_filter_survey'), ref: 'survey' },
    { title: t('analytics_column_question'), ref: 'question' },
    { title: t('analytics_column_question_type'), ref: 'type' },
    { title: t('analytics_kpi_survey_responses'), ref: 'responseCount', renderCell: ({ row }) => formatNumber(row.responseCount) },
    { title: t('analytics_column_average'), ref: 'average', renderCell: ({ row }) => row.average == null ? '—' : formatNumber(row.average) },
  ], [formatNumber, t])

  const surveyAnswerColumns = useMemo(() => [
    { title: t('analytics_filter_survey'), ref: 'survey' },
    { title: t('analytics_column_question'), ref: 'question' },
    { title: t('analytics_column_answer_option'), ref: 'option' },
    { title: t('analytics_total'), ref: 'count', renderCell: ({ row }) => formatNumber(row.count) },
    { title: t('analytics_column_percentage'), ref: 'percentage', renderCell: ({ row }) => formatPercent(row.percentage) },
  ], [formatNumber, formatPercent, t])

  const tableProps = {
    analytical: true,
    direction: filters.direction,
    onSort: setSort,
    sort: filters.sort,
  }

  const hubColumns = [
    { title: t('analytics_hub_page'), ref: 'pagePath', renderCell: ({ row }) => row.pagePath.endsWith('/assistants') ? 'AI Assistants' : t(`competitive_intelligence_nav_${row.pagePath.split('/').pop()}`) },
    { title: t('analytics_column_views'), ref: 'views', renderCell: ({ row }) => formatNumber(row.views) },
    { title: t('analytics_column_unique_users'), ref: 'uniqueViewers', renderCell: ({ row }) => formatNumber(row.uniqueViewers) },
  ]

  return (
    <SectionRoot key={section}>
      {kpis.length ? <AnalyticsKpis items={kpis} t={t} /> : null}

      {section === 'overview' ? (
        <>
          <AnalyticsTable columns={hubColumns} rows={data.competitiveHubUsage} tableProps={{ analytical: true, minWidth: 0 }} t={t} titleKey="analytics_hub_traffic" />
          <ChartPanel wide>
            <PanelHeader><div><PanelTitle>{t('analytics_chart_usage_over_time')}</PanelTitle></div></PanelHeader>
            <AnalyticsChart
              ariaLabel={t('analytics_chart_usage_over_time')}
              categories={(data.timeline || []).map(item => formatDate(item.date))}
              formatValue={formatNumber}
              series={buildSeries(data.timeline, [
                ['activeUsers', t('analytics_kpi_active_users')],
                ['sessions', t('analytics_kpi_sessions')],
                ['activities', t('analytics_column_activities')],
              ])}
              t={t}
              type="area"
            />
          </ChartPanel>
          {!filters.area ? <ChartPanel wide>
            <PanelHeader><div><PanelTitle>{t('analytics_chart_area_comparison')}</PanelTitle></div></PanelHeader>
            <AnalyticsChart ariaLabel={t('analytics_chart_area_comparison')} categories={(data.areaComparison || []).map(item => t(PORTAL_LABELS[item.area]))} formatValue={formatNumber} series={buildSeries(data.areaComparison, [
              ['activeUsers', t('analytics_kpi_active_users')], ['sessions', t('analytics_kpi_sessions')], ['contentViews', t('analytics_kpi_content_views')],
              ['videoViews', t('analytics_kpi_video_views')], ['downloads', t('analytics_kpi_downloads')], ['certificates', t('analytics_kpi_certificates_issued')], ['aiInteractions', t('analytics_column_ai_interactions')],
            ])} t={t} type="bar" />
          </ChartPanel> : null}
          <TwoColumns>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_usage_by_persona')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                activityPalette
                ariaLabel={t('analytics_chart_usage_by_persona')}
                categories={(data.personaUsage || []).map(item => item.persona || item.label)}
                formatValue={formatNumber}
                horizontal
                series={personaActivitySeries(data.personaUsage, t)}
                stacked
                t={t}
                type="bar"
              />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_most_used_content')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                ariaLabel={t('analytics_chart_most_used_content')}
                categories={(data.topContent || []).slice(0, 7).map(item => `${title(item)}${item.area ? ` · ${t(PORTAL_LABELS[item.area])}` : ''}`)}
                formatValue={formatNumber}
                horizontal
                series={[{ name: t('analytics_column_views'), data: (data.topContent || []).slice(0, 7).map(item => Number(first(item, ['views']))) }]}
                t={t}
                type="bar"
              />
            </ChartPanel>
          </TwoColumns>
          <TwoColumns>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_onboarding_funnel')}</PanelTitle></div></PanelHeader>
              <FunnelChart data={data.onboarding?.funnel} formatNumber={formatNumber} t={t} />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_completion_to_certificate')}</PanelTitle></div></PanelHeader>
              <FunnelChart data={certificateJourney} formatNumber={formatNumber} t={t} />
            </ChartPanel>
          </TwoColumns>
        </>
      ) : null}

      {section === 'learning' ? (
        <>
          <Grid>
            <ChartPanel wide>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_course_views_over_time')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_course_views_over_time')} categories={(data.viewsOverTime || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.viewsOverTime, [['views', t('analytics_column_views')], ['uniqueViewers', t('analytics_column_unique_users')], ['completed', t('analytics_column_completed')]])} t={t} type="area" />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_completion_funnel')}</PanelTitle></div></PanelHeader>
              <FunnelChart data={data.completionFunnel} formatNumber={formatNumber} t={t} />
            </ChartPanel>
          </Grid>
          <Grid three>
            <RankingPanel formatNumber={formatNumber} items={data.topCourses} t={t} titleKey="analytics_chart_top_courses" valueKey="views" />
            <RankingPanel formatNumber={formatNumber} items={data.mostCompleted} t={t} titleKey="analytics_chart_most_completed" valueKey="completed" />
            <RankingPanel formatNumber={formatNumber} formatPercent={formatPercent} items={data.lowestCompletion} percent t={t} titleKey="analytics_chart_lowest_completion" valueKey="completionRate" />
          </Grid>
          <AnalyticsTable columns={learningColumns} rows={data.courseRows} tableProps={tableProps} t={t} titleKey="analytics_table_courses" />
        </>
      ) : null}

      {section === 'videos' ? (
        <>
          <Grid>
            <ChartPanel wide><PanelHeader><div><PanelTitle>{t('analytics_chart_video_views_over_time')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_video_views_over_time')} categories={(data.viewsOverTime || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.viewsOverTime, [['views', t('analytics_column_views')], ['completed', t('analytics_column_completed')]])} t={t} type="area" /></ChartPanel>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_retention_funnel')}</PanelTitle></div></PanelHeader><FunnelChart data={data.retentionFunnel} formatNumber={formatNumber} t={t} /></ChartPanel>
          </Grid>
          <Grid three>
            <RankingPanel formatNumber={formatNumber} items={data.mostWatched} t={t} titleKey="analytics_most_watched" valueKey="plays" />
            <RankingPanel formatPercent={formatPercent} items={data.highestCompletion} percent t={t} titleKey="analytics_highest_completion" valueKey="completionRate" />
            <RankingPanel formatPercent={formatPercent} items={data.lowestCompletion} percent t={t} titleKey="analytics_chart_lowest_completion" valueKey="completionRate" />
          </Grid>
          <AnalyticsTable columns={videoColumns} rows={data.videoRows} tableProps={tableProps} t={t} titleKey="analytics_table_videos" />
        </>
      ) : null}

      {section === 'files' ? (
        <>
          <ChartPanel wide><PanelHeader><div><PanelTitle>{t('analytics_chart_downloads_over_time')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_downloads_over_time')} categories={(data.downloadsOverTime || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.downloadsOverTime, [['downloads', t('analytics_column_downloads')], ['uniqueDownloaders', t('analytics_kpi_unique_downloaders')]])} t={t} type="area" /></ChartPanel>
          <TwoColumns>
            <RankingPanel formatNumber={formatNumber} items={data.mostDownloaded} t={t} titleKey="analytics_most_downloaded" valueKey="downloads" />
            <RankingPanel formatNumber={formatNumber} items={data.withoutDownloads} t={t} titleKey="analytics_files_without_downloads" valueKey="downloads" />
          </TwoColumns>
          <AnalyticsTable columns={fileColumns} rows={data.fileRows} tableProps={tableProps} t={t} titleKey="analytics_table_files" />
        </>
      ) : null}

      {section === 'onboarding' ? (
        <>
          <ChartPanel wide><PanelHeader><div><PanelTitle>{t('analytics_chart_onboarding_funnel')}</PanelTitle></div></PanelHeader><FunnelChart data={data.funnel} formatNumber={formatNumber} t={t} /></ChartPanel>
          <AnalyticsTable columns={onboardingColumns} rows={data.noWatchUsers} tableProps={tableProps} t={t} titleKey="analytics_logged_in_not_watched_title" />
        </>
      ) : null}

      {section === 'certificates' ? (
        <>
          <Grid three>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_certificate_status')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_certificate_status')} categories={Object.keys(data.statusBreakdown || {}).map(key => t(KPI_LABELS[key] || `analytics_kpi_${key}`))} formatValue={formatNumber} series={Object.values(data.statusBreakdown || {}).map(Number)} t={t} type="donut" /></ChartPanel>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_course_certificate_coverage')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_course_certificate_coverage')} categories={[t('analytics_kpi_courses_total'), t('analytics_kpi_courses_with_certificates'), t('analytics_kpi_courses_without_certificates')]} formatValue={formatNumber} series={[{ name: t('analytics_kpi_courses_total'), data: [Number(data.courseCoverage?.total || 0), Number(data.courseCoverage?.withCertificates || 0), Number(data.courseCoverage?.withoutCertificates || 0)] }]} t={t} type="bar" /></ChartPanel>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_completion_to_certificate')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_completion_to_certificate')} categories={[t('analytics_kpi_courses_completed'), t('analytics_kpi_certificates_issued')]} formatValue={formatNumber} series={[{ name: t('analytics_kpi_users'), data: [Number(data.completionToCertificate?.completed || 0), Number(data.completionToCertificate?.certified || 0)] }]} t={t} type="bar" /></ChartPanel>
          </Grid>
          <AnalyticsTable columns={certificateColumns} rows={data.certificateRows || []} tableProps={tableProps} t={t} titleKey="analytics_table_certificates" />
        </>
      ) : null}

      {section === 'users' ? (
        <>
          <Grid>
            <ChartPanel wide><PanelHeader><div><PanelTitle>{t('analytics_chart_active_users_over_time')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_active_users_over_time')} categories={(data.activeUsersOverTime || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.activeUsersOverTime, [['activeUsers', t('analytics_kpi_active_users')]])} t={t} type="area" /></ChartPanel>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_new_vs_returning')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_new_vs_returning')} categories={(data.newVsReturning || []).map(item => t(item.key === 'new' ? 'analytics_kpi_new_users' : 'analytics_kpi_returning_users'))} formatValue={formatNumber} series={[{ name: t('analytics_kpi_active_users'), data: (data.newVsReturning || []).map(item => Number(first(item, ['value']))) }]} t={t} type="bar" /></ChartPanel>
          </Grid>
          <TwoColumns>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_usage_by_persona')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_usage_by_persona')} categories={(data.usageByPersona || []).map(item => item.persona || item.label)} formatValue={formatNumber} horizontal series={[{ name: t('analytics_kpi_active_users'), data: (data.usageByPersona || []).map(item => Number(first(item, ['activeUsers', 'users', 'value']))) }]} t={t} type="bar" /></ChartPanel>
            <ChartPanel><PanelHeader><div><PanelTitle>{t('analytics_chart_usage_by_locale')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_usage_by_locale')} categories={(data.usageByLocale || []).map(item => item.locale || item.label)} formatValue={formatNumber} series={(data.usageByLocale || []).map(item => Number(first(item, ['activities', 'activeUsers', 'users', 'value'])))} t={t} type="donut" /></ChartPanel>
          </TwoColumns>
          <ChartPanel wide><PanelHeader><div><PanelTitle>{t('analytics_chart_activity_distribution')}</PanelTitle></div></PanelHeader><AnalyticsChart ariaLabel={t('analytics_chart_activity_distribution')} categories={(data.activityDistribution || []).map(item => item.label || item.bucket || item.key)} formatValue={formatNumber} series={[{ name: t('analytics_kpi_users'), data: (data.activityDistribution || []).map(item => Number(first(item, ['users', 'value']))) }]} t={t} type="bar" /></ChartPanel>
          <AnalyticsTable columns={userColumns} rows={data.mostActiveUsers} tableProps={tableProps} t={t} titleKey="analytics_chart_most_active_users" />
        </>
      ) : null}

      {section === 'activity' ? (
        <>
          <Grid>
            <ChartPanel wide>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_usage_over_time')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                ariaLabel={t('analytics_chart_usage_over_time')}
                categories={(data.activityOverview?.timeline || []).map(item => formatDate(item.date))}
                formatValue={formatNumber}
                series={buildSeries(data.activityOverview?.timeline, [
                  ['activities', t('analytics_column_activities')],
                  ['activeUsers', t('analytics_kpi_active_users')],
                ])}
                t={t}
                type="area"
              />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_usage_by_persona')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                activityPalette
                ariaLabel={t('analytics_chart_usage_by_persona')}
                categories={(data.activityOverview?.personaUsage || []).map(item => item.persona || item.label)}
                formatValue={formatNumber}
                horizontal
                series={personaActivitySeries(data.activityOverview?.personaUsage, t)}
                stacked
                t={t}
                type="bar"
              />
            </ChartPanel>
          </Grid>
          <Grid>
            <ChartPanel wide>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_activity_by_type')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                ariaLabel={t('analytics_chart_activity_by_type')}
                categories={(data.activityBreakdown || []).map(item => t(ACTIVITY_BREAKDOWN_LABELS[item.action] || 'analytics_column_activities'))}
                dataLabels
                formatValue={formatNumber}
                height={Math.max(300, (data.activityBreakdown || []).length * 52)}
                horizontal
                series={[{ name: t('analytics_column_activities'), data: (data.activityBreakdown || []).map(item => Number(first(item, ['count', 'value']))) }]}
                t={t}
                type="bar"
              />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_activity_by_portal')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart
                ariaLabel={t('analytics_chart_activity_by_portal')}
                categories={(data.activityOverview?.portalUsage || []).map(item => t(PORTAL_LABELS[item.portal] || 'analytics_portal_universe'))}
                dataLabels
                formatValue={formatNumber}
                series={(data.activityOverview?.portalUsage || []).map(item => Number(first(item, ['activities', 'value']))) }
                t={t}
                type="donut"
              />
            </ChartPanel>
          </Grid>
        </>
      ) : null}

      {section === 'surveys' ? (
        <>
          <Grid>
            <ChartPanel wide>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_survey_usage_over_time')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_survey_usage_over_time')} categories={(data.timeline || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.timeline, [
                ['views', t('analytics_kpi_survey_views')],
                ['starts', t('analytics_kpi_survey_starts')],
                ['completions', t('analytics_kpi_survey_completions')],
              ])} t={t} type="area" />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_survey_funnel')}</PanelTitle></div></PanelHeader>
              <FunnelChart data={data.funnel} formatNumber={formatNumber} t={t} />
            </ChartPanel>
          </Grid>
          <AnalyticsTable columns={surveyColumns} rows={data.surveyRows || []} tableProps={tableProps} t={t} titleKey="analytics_table_surveys" />
          <AnalyticsTable columns={surveyQuestionColumns} descriptionKey="analytics_survey_privacy_note" rows={data.questionAnalytics || []} tableProps={{ analytical: true }} t={t} titleKey="analytics_table_survey_questions" />
          <AnalyticsTable columns={surveyAnswerColumns} rows={data.answerDistribution || []} tableProps={{ analytical: true }} t={t} titleKey="analytics_table_survey_answers" />
        </>
      ) : null}

      {section === 'competitive' ? (
        <>
          <ChartPanel wide>
            <PanelHeader><div><PanelTitle>{t('analytics_chart_competitive_usage_over_time')}</PanelTitle><PanelDescription>{t('analytics_chart_competitive_usage_over_time_description')}</PanelDescription></div></PanelHeader>
            <AnalyticsChart ariaLabel={t('analytics_chart_competitive_usage_over_time')} categories={(data.timeline || []).map(item => formatDate(item.date))} formatValue={formatNumber} series={buildSeries(data.timeline, [
              ['users', t('analytics_kpi_active_users')], ['sessions', t('analytics_kpi_sessions')], ['views', t('analytics_kpi_total_views')],
              ['searches', t('analytics_kpi_searches')], ['aiUsage', t('analytics_kpi_ai_prompts')],
            ])} t={t} type="area" />
          </ChartPanel>
          <Grid>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_section_usage')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_section_usage')} categories={(data.sectionUsage || []).map(item => item.section)} formatValue={formatNumber} horizontal series={buildSeries(data.sectionUsage, [
                ['views', t('analytics_column_views')], ['sessions', t('analytics_column_sessions')], ['engagement', t('analytics_column_engagement')],
              ])} t={t} type="bar" />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_content_to_ai')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_content_to_ai')} categories={[t('analytics_content_sessions'), t('analytics_converted_sessions')]} dataLabels formatValue={formatNumber} horizontal series={[{ name: t('analytics_column_sessions'), data: [Number(data.contentToAi?.contentSessions || 0), Number(data.contentToAi?.convertedSessions || 0)] }]} t={t} type="bar" />
              <PanelDescription>{t('analytics_conversion_rate')}: {formatPercent(data.contentToAi?.conversionRate || 0)}</PanelDescription>
            </ChartPanel>
          </Grid>
          <Grid>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_competitor_interest')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_competitor_interest')} categories={(data.competitorRows || []).slice(0, 10).map(item => item.competitor)} formatValue={formatNumber} horizontal series={buildSeries((data.competitorRows || []).slice(0, 10), [
                ['views', t('analytics_column_views')], ['searches', t('analytics_kpi_searches')], ['aiInteractions', t('analytics_column_ai_interactions')], ['sourceOpens', t('analytics_kpi_source_opens')],
              ])} t={t} type="bar" />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_search_quality')}</PanelTitle><PanelDescription>{t('analytics_search_privacy_note')}</PanelDescription></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_search_quality')} categories={[t('analytics_kpi_searches'), t('analytics_search_no_results'), t('analytics_search_result_clicks')]} dataLabels formatValue={formatNumber} series={[{ name: t('analytics_kpi_searches'), data: [Number(data.search?.total || 0), Number(data.search?.noResults || 0), Number(data.search?.resultClicks || 0)] }]} t={t} type="bar" />
            </ChartPanel>
          </Grid>
          <AnalyticsTable columns={competitiveContentColumns} rows={data.contentRows || []} tableProps={tableProps} t={t} titleKey="analytics_table_competitive_content" />
          <AnalyticsTable columns={assistantColumns} rows={data.assistantRows || []} tableProps={{ analytical: true }} t={t} titleKey="analytics_table_ai_assistants" />
          <AnalyticsTable columns={competitiveFilterColumns} rows={data.filterRows || []} tableProps={{ analytical: true }} t={t} titleKey="analytics_table_competitive_filters" />
          <Grid>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_pipeline_30_days')}</PanelTitle><PanelDescription>{t('analytics_pipeline_separate_note')}</PanelDescription></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_pipeline_30_days')} categories={[t('analytics_pipeline_runs'), t('analytics_pipeline_items'), t('analytics_column_errors')]} dataLabels formatValue={formatNumber} series={[{ name: t('analytics_chart_pipeline_30_days'), data: [Number(data.operational?.last30Days?.runs || 0), Number(data.operational?.last30Days?.items_found || 0), Number(data.operational?.last30Days?.errors || 0)] }]} t={t} type="bar" />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_chart_pipeline_coverage')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_chart_pipeline_coverage')} categories={(data.operational?.coverageByCompetitor || []).slice(0, 10).map(item => item.competitor)} formatValue={formatNumber} horizontal series={[{ name: t('analytics_pipeline_items'), data: (data.operational?.coverageByCompetitor || []).slice(0, 10).map(item => Number(item.items || 0)) }]} t={t} type="bar" />
            </ChartPanel>
          </Grid>
        </>
      ) : null}

      {section === 'invitations' ? (
        <>
          <TwoColumns>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_invitation_funnel')}</PanelTitle></div></PanelHeader>
              <FunnelChart data={data.funnel} formatNumber={formatNumber} t={t} />
            </ChartPanel>
            <ChartPanel>
              <PanelHeader><div><PanelTitle>{t('analytics_retention')}</PanelTitle></div></PanelHeader>
              <AnalyticsChart ariaLabel={t('analytics_retention')} categories={['D1', 'D7', 'D30']} dataLabels formatValue={formatPercent} series={[{ name: t('analytics_retention'), data: [data.retention?.d1?.rate || 0, data.retention?.d7?.rate || 0, data.retention?.d30?.rate || 0] }]} t={t} type="bar" />
            </ChartPanel>
          </TwoColumns>
          <AnalyticsTable columns={invitationColumns} onRowClick={setSelectedInvitation} rows={data.performance || []} tableProps={tableProps} t={t} titleKey="analytics_invitation_performance" />
          {selectedInvitation ? <ChartPanel wide>
            <PanelHeader><div><PanelTitle>{selectedInvitation.invitation}</PanelTitle><PanelDescription>{t('analytics_invitation_drilldown_description')}</PanelDescription></div></PanelHeader>
            <AnalyticsChart ariaLabel={t('analytics_invitation_engagement')} categories={[
              t('analytics_kpi_active_users'), t('analytics_kpi_sessions'), t('analytics_kpi_content_views'),
              t('analytics_kpi_video_views'), t('analytics_kpi_downloads'), t('analytics_kpi_certificates_issued'),
              t('analytics_kpi_searches'), t('analytics_column_ai_interactions'),
            ]} dataLabels formatValue={formatNumber} horizontal series={[{ name: t('analytics_invitation_engagement'), data: [
              selectedInvitation.engagement?.activeUsers || 0, selectedInvitation.engagement?.sessions || 0,
              selectedInvitation.engagement?.contentViews || 0, selectedInvitation.engagement?.videoViews || 0,
              selectedInvitation.engagement?.downloads || 0, selectedInvitation.engagement?.certificates || 0,
              selectedInvitation.engagement?.searches || 0, selectedInvitation.engagement?.aiUsage || 0,
            ] }]} t={t} type="bar" />
          </ChartPanel> : null}
          <AnalyticsTable columns={creatorColumns} rows={data.creators || []} tableProps={{ analytical: true }} t={t} titleKey="analytics_invitation_creators" />
        </>
      ) : null}
    </SectionRoot>
  )
}

function FunnelChart({ data, formatNumber, t }) {
  const funnel = normalizeFunnel(data)
  return <AnalyticsChart ariaLabel={t('analytics_chart_completion_funnel')} categories={funnel.map(item => t(FUNNEL_LABELS[item.key] || KPI_LABELS[item.key] || 'analytics_column_activity'))} dataLabels formatValue={formatNumber} horizontal series={[{ name: t('analytics_kpi_users'), data: funnel.map(item => item.value) }]} t={t} type="bar" />
}

function RankingList({ formatNumber = value => value, formatPercent = value => value, items = [], percent, valueKey }) {
  return (
    <Ranking>
      {(items || []).slice(0, 8).map((item, index) => (
        <RankingItem key={item.id || `${title(item)}-${index}`}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <div><RankingTitle title={title(item)}>{title(item)}</RankingTitle><RankingMeta>{percent ? formatPercent(first(item, [valueKey])) : formatNumber(first(item, [valueKey]))}</RankingMeta></div>
        </RankingItem>
      ))}
    </Ranking>
  )
}

function RankingPanel({ descriptionKey, formatNumber, formatPercent, items, percent, t, titleKey, valueKey }) {
  return <ChartPanel><PanelHeader><div><PanelTitle>{t(titleKey)}</PanelTitle>{descriptionKey ? <PanelDescription>{t(descriptionKey)}</PanelDescription> : null}</div></PanelHeader><RankingList formatNumber={formatNumber} formatPercent={formatPercent} items={items} percent={percent} valueKey={valueKey} /></ChartPanel>
}

function AnalyticsTable({ columns, descriptionKey, onRowClick, rows = [], tableProps, t, titleKey }) {
  const normalizedRows = (rows || []).map((row, index) => ({
    ...row,
    id: row.id || row.courseId || row.videoId || row.fileId || index,
    courseTitle: first(row, ['courseTitle', 'course', 'title', 'name'], '—'),
    displayName: first(row, ['displayName', 'userName', 'name'], '—'),
    title: title(row),
  }))
  return <TablePanel><PanelHeader><div><PanelTitle>{t(titleKey)}</PanelTitle>{descriptionKey ? <PanelDescription>{t(descriptionKey)}</PanelDescription> : null}</div></PanelHeader><BasicTable {...tableProps} columns={columns} onRowClick={onRowClick} rows={normalizedRows} /></TablePanel>
}
